import { useEffect, useState } from "react";
import dashboardService from "../../../services/dashboard";
import { DREHierarchicalLine, DREHierarchicalNode, DREHierarchicalResponse, DashboardFilter } from "../../../types/dashboard";
import moment, { Moment } from "moment"
import { formatMomentPtBr } from "../../../utils/dateRange";
import { toggleDREItem, useExpandedDREItems, clearExpandedDREItems, setEndDate, setStartDate, useStartDate, useEndDate } from "@store/dreDate/dreDateSlice";
import { useAppDispatch } from "@store";
import { ExpandNodeParams } from "../DRETable";

const dreTypeSelectOptions = [
  { id: 'forecastValue', label: 'Previsão' },
  { id: 'committedValue', label: 'Comprometido' },
  { id: 'actualValue', label: 'Realizado' },
  { id: 'forecastVariance', label: 'Previsto X Realizado' },
];

export type dreTypeOption = 'forecastValue' | 'committedValue' | 'actualValue' | 'forecastVariance'

const useDREDate = (requestFilters?: DashboardFilter) => {
  const dispatch = useAppDispatch();
  const [dre, setDre] = useState<DREHierarchicalResponse | null>(null);
  const [dreLoading, setDreLoading] = useState(true);
  const [showBody, setShowBody] = useState(false)
  const [monthsLabels, setMonthsLabels] = useState<string[]>([])
  const [dreLoadingNodes, setDreLoadingNodes] = useState<string[]>([]);
  const [dreTypeSelect, setDreTypeSelect] = useState<dreTypeOption>('actualValue')
  const [hoveredId, setHoveredId] = useState('');

  const startDate = useStartDate()
  const endDate = useEndDate()

  const expandedDREItems = useExpandedDREItems();

  useEffect(() => {
    if(!startDate) {
      const currentDate = moment()
      setMonthsLabels(getThreeMonthLabels(currentDate));
      return
    }

    const currentDate = moment(startDate)
    setMonthsLabels(getThreeMonthLabels(currentDate));
  }, [showBody])

  const fetchDRE = async (startDate?: string, endDate?: string) => {
    setDreLoading(true);
    try {
      const data = await dashboardService.getVolksTotalDREEvolution({...requestFilters, startDate, endDate});
      setDre(data);
    } catch (error) {
      console.error("Error fetching DRE data:", error);
    } finally {
      setDreLoading(false);
    }
  }

  const getThreeMonthLabels = (selected: Moment): string[] => {
    const current = selected.clone();
    const firstDayOfMonth = current.clone().startOf("month").format("YYYY-MM-DD");
    const lastDayOfMonth = current.clone().endOf("month").format("YYYY-MM-DD");

    fetchDRE(firstDayOfMonth, lastDayOfMonth);
    dispatch(setStartDate(firstDayOfMonth))
    dispatch(setEndDate(lastDayOfMonth))

    const previous = current.clone().subtract(1, "month");
    const next = current.clone().add(1, "month");

    return [
      formatMomentPtBr(previous, "MMMM"),
      formatMomentPtBr(current, "MMMM"),
      formatMomentPtBr(next, "MMMM"),
    ];
  }

  const toggleDRE = (id: string) => {
    dispatch(toggleDREItem(id));
  };

  const updateNodeInChildren = (
    nodes: DREHierarchicalNode[],
    nodeId: string,
    newChildren: DREHierarchicalNode[]
  ): DREHierarchicalNode[] => {
    return nodes.map((node) => {
      const currentNodeId = node.compositeId ?? node.id;
      if (currentNodeId === nodeId) {
        return { ...node, children: newChildren };
      }
      if (node.children && node.children.length > 0) {
        const updatedChildren = updateNodeInChildren(node.children, nodeId, newChildren);
        const wasUpdated = updatedChildren.some((child, idx) => child !== node.children![idx]);
        if (wasUpdated) {
          return { ...node, children: updatedChildren };
        }
      }
      return node;
    });
  };

  const updateNodeChildren = (
    lines: DREHierarchicalLine[],
    nodeId: string,
    children: DREHierarchicalNode[]
  ): DREHierarchicalLine[] => {
    const newLines = lines.map((line) => {
      if (line.children) {
        const updatedChildren = updateNodeInChildren(line.children, nodeId, children);
        const wasUpdated = updatedChildren.some((child, idx) => child !== line.children![idx]);
        if (wasUpdated) {
          return { ...line, children: updatedChildren };
        }
      }
      return line;
    });
    return newLines;
  };

  const handleExpandDRENode = async (params: ExpandNodeParams) => {
    const { nodeId, compositeId, nodeType, lineType, parentPath } = params;

    setDreLoadingNodes((prev) => [...prev, compositeId]);

    try {
      const response = await dashboardService.expandDRENode({startDate, endDate}, {
        nodeId,
        nodeType,
        lineType,
        parentPath,
        getEvolutionValues: true,
      });

      if (response.children && response.children.length > 0) {
        setDre((prevDre) => {
          if (!prevDre) {
            return prevDre;
          }
          const newLines = updateNodeChildren(prevDre.lines, compositeId, response.children);
          return {
            ...prevDre,
            lines: newLines,
          };
        });
      }
    } catch (error) {
      console.error("Error expanding DRE node:", error);
    } finally {
      setDreLoadingNodes((prev) => prev.filter((id) => id !== compositeId));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }

  const closeAllNodes = () => {
    dispatch(clearExpandedDREItems());
  };

  return {
    dre,
    dreLoading,
    showBody,
    setShowBody,
    monthsLabels,
    getThreeMonthLabels,
    setMonthsLabels,
    handleExpandDRENode,
    toggleDRE,
    dreLoadingNodes,
    expandedDREItems,
    closeAllNodes,
    dreTypeSelect,
    setDreTypeSelect,
    dreTypeSelectOptions,
    startDate,
    hoveredId,
    setHoveredId
  }

}

export default useDREDate
